import { Bluetooth, BluetoothOff, ChevronRightIcon, Usb } from 'lucide-react';
import Image from 'next/image';
import { FormattedMessage } from 'react-intl';

import deviceConnected from '@/assets/image/chronicle/device-connected-status.png';
import deviceDisconnected from '@/assets/image/chronicle/device-disconnected-status.png';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';

import type { DeviceStatusPopoverProps } from './device-status-popover.types';

/**
 * Content card for the Chronicle device status popover.
 * Shows device photo, name, connection status, and a chevron to navigate to devices page.
 */
export function DeviceStatusPopover({
  isConnected,
  isUsbConnected,
  onNavigateToDevices,
}: DeviceStatusPopoverProps) {
  // Only treat as USB mode when BLE is down, so a connected device isn't
  // flipped to USB by a foreign/stale mount. Photo counts either as "present".
  const isUsbMode = isUsbConnected && !isConnected;
  const showConnectedPhoto = isConnected || isUsbConnected;

  // Status caption: connection-method icon + label
  // Offline-transcribing messaging lives on RemoteStatusCard / header badge —
  // this popover only reports connection method (HAR-1333).
  const captionIconClass = 'shrink-0 text-text-secondary';
  const status = isUsbMode
    ? {
        icon: <Usb className={captionIconClass} aria-hidden="true" />,
        label: <FormattedMessage defaultMessage="USB Transfer Mode" id="9p9jBA9/F5" />,
      }
    : isConnected
      ? {
          icon: <Bluetooth className={captionIconClass} aria-hidden="true" />,
          label: <FormattedMessage defaultMessage="Bluetooth" id="c9GazsqfYI" />,
        }
      : {
          icon: <BluetoothOff className={captionIconClass} aria-hidden="true" />,
          label: <FormattedMessage defaultMessage="Disconnected" id="FZeQlcp+xX" />,
        };
  return (
    <Button
      size="lg"
      variant="ghost"
      className="group flex h-auto w-full items-center gap-3 rounded-sm px-3 py-2 hover:bg-surface-hover"
      onClick={onNavigateToDevices}
      data-testid="device-status-popover-card"
    >
      {/* Device image with circle background — default: accent, hover: white */}
      <div className="relative size-10 shrink-0">
        <div className="relative size-10 overflow-hidden rounded-full">
          <div className="absolute inset-0 bg-surface-accent transition-colors duration-200 ease-out group-hover:bg-surface" />
          <Image
            src={showConnectedPhoto ? deviceConnected : deviceDisconnected}
            alt=""
            width={14}
            height={42}
            className="absolute left-1/2 top-1/2 h-[42px] w-[14px] -translate-x-1/2 translate-y-[calc(-50%+7px)] object-contain"
            aria-hidden="true"
          />
        </div>
        {/* Status dot — orange in USB transfer mode, green when BLE connected */}
        {isUsbMode ? (
          <div
            className="absolute bottom-0 end-0 size-2.5 rounded-full bg-validation-warning"
            aria-hidden="true"
          />
        ) : (
          isConnected && (
            <div
              className="absolute bottom-0 end-0 size-2.5 rounded-full bg-validation-success"
              aria-hidden="true"
            />
          )
        )}
      </div>

      {/* Device info */}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
        <TypographyP2 className="text-text-primary">
          <FormattedMessage defaultMessage="Heidi Remote" id="2LXo+tn89I" />
        </TypographyP2>
        <span className="flex items-center gap-1">
          {status.icon}
          <TypographyCaption className="text-text-secondary">
            {status.label}
          </TypographyCaption>
        </span>
      </div>

      {/* Chevron */}
      <ChevronRightIcon className="shrink-0 text-text-tertiary rtl:-scale-x-100" />
    </Button>
  );
}
